import {FissionChatBotOptions} from '@/types/fission-chat-bot-options';
import {BusinessRole} from '@/types/businessRole';
import {ChatbotEnv} from '@/types/chatbotEnv';
import {MAX_RETRY_ATTEMPTS, RETRY_DELAY_MS} from './constants';

/**
 * Interface for Fission Chatbot API response
 */
interface FissionChatbotResponse {
    status: string;
    conversationId: string;
    message: string;
    requestId: string;
}

/**
 * Interface for the formatted response
 */
interface FormattedResponse {
    content: string;
    metadata?: {
        conversationId?: string;
        requestId?: string;
        status?: string;
    };
}

/**
 * Service for handling communication with the Fission multi-agent chatbot function
 */
export class FissionChatbotService {
    private fissionUrl: string;

    /**
     * Creates a new instance of FissionChatbotService
     * @param fissionUrl The URL of the Fission function
     */
    constructor(fissionUrl: string) {
        this.fissionUrl = fissionUrl;
    }

    /**
     * Sends a request to the Fission multi-agent chatbot function
     * @param params Request parameters including user message, chat history, environment, and business role
     * @returns Promise resolving to the formatted response
     */
    public async sendRequest(params: {
        userMessage: string;
        environment: ChatbotEnv;
        businessRole?: string;
        userId: string;
        conversationId: string;
        isFastmodeActive?: boolean;
        isPresentationMode?: boolean;
        referenceId?: string;
    }): Promise<FormattedResponse> {
        const {userMessage, environment, businessRole, userId, conversationId, isFastmodeActive, isPresentationMode, referenceId} = params;

        console.log('[TEST] FissionChatbotService - Sending request with conversationId:', conversationId);
        console.log('[TEST] FissionChatbotService - User message:', userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : ''));

        // Prepare the request payload according to FissionChatBotOptions
        const requestPayload: FissionChatBotOptions = {
            userId,
            conversationId,
            message: userMessage,
            businessRole: (businessRole as BusinessRole) || BusinessRole.PLANNER, // Default to PLANNER if not provided
            chatbotEnv: environment,
            isFastmodeActive: isFastmodeActive !== undefined ? isFastmodeActive : true, // Default to FastMode if not provided
            isPresentationMode: isPresentationMode !== undefined ? isPresentationMode : false,
            referenceId: referenceId !== undefined ? referenceId : '',
        };

        console.log('[TEST] FissionChatbotService - Request payload prepared, sending to Fission function');
        
        // Send the request with retry logic
        return this.sendRequestWithRetry(requestPayload);
    }

    /**
     * Sends a request to the Fission function with retry logic
     * @param requestPayload The request payload to send
     * @returns Promise resolving to the formatted response
     */
    private async sendRequestWithRetry(requestPayload: FissionChatBotOptions): Promise<FormattedResponse> {
        let attempts = 0;

        while (attempts < MAX_RETRY_ATTEMPTS) {
            try {
                console.log('Sending request to Fission function:', this.fissionUrl);
                console.log('Request payload:', JSON.stringify(requestPayload, null, 2));

                const response = await fetch(this.fissionUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestPayload)
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Fission function error:', errorText);
                    throw new Error(`Fission function error: ${response.status} ${response.statusText}`);
                }

                const data: FissionChatbotResponse = await response.json();
                console.log('Fission function response:', data);
                console.log('[TEST] FissionChatbotService - Received response from Fission function');
                console.log('[TEST] FissionChatbotService - Response status:', data.status);
                console.log('[TEST] FissionChatbotService - Response conversationId:', data.conversationId);
                console.log('[TEST] FissionChatbotService - Response message preview:',
                    data.message.substring(0, 50) + (data.message.length > 50 ? '...' : ''));
                console.log('[TEST] FissionChatbotService - The AI response will be saved by the Fission function, not the frontend');

                // Format the response to match the expected structure
                return {
                    content: data.message,
                    metadata: {
                        conversationId: data.conversationId,
                        requestId: data.requestId,
                        status: data.status
                    }
                };
            } catch (error) {
                attempts++;
                console.error(`Fission function request failed (attempt ${attempts}/${MAX_RETRY_ATTEMPTS}):`, error);

                // If we've reached the maximum number of attempts, throw the error
                if (attempts >= MAX_RETRY_ATTEMPTS) {
                    throw error;
                }

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            }
        }

        // This should never be reached due to the throw in the loop,
        // but TypeScript requires a return statement
        throw new Error('Maximum retry attempts exceeded');
    }

    /**
     * Updates the Fission function URL
     * @param fissionUrl The new Fission function URL
     */
    public updateFissionUrl(fissionUrl: string): void {
        this.fissionUrl = fissionUrl;
    }
}

// Export a singleton instance with the provided Fission function URL
export const fissionChatbotService = new FissionChatbotService((process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'multi-agent-chatbot');
